import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import AuthContext from './AuthContext';
import * as constants from '../common/constants';
import * as JspUtil from '../common/JspUtil';
import * as actions from '../loginpages/actionCreators';
import { getInitialRoute, jspGlobals } from '../common/jspGlobals';

const PUBLIC_ROUTES = [
    '/registration',
    constants.ROUTE_PAGE_REQUEST_GLOBAL_ID,
    constants.ROUTE_PAGE_REGISTER_GLOBAL_ID_WITH_COMPANY,
    constants.ROUTE_PAGE_CONFIRM_USER_EMAIL,
    constants.ROUTE_REQUEST_GLOBAL_ID_SERVLET,
    constants.ROUTE_REGISTER_GLOBAL_ID_WITH_COMPANY_SERVLET,
    constants.ROUTE_CONFIRM_USER_EMAIL_SERVLET,
    constants.ROUTE_PAGE_SAML_MESSAGE,
    constants.ROUTE_PAGE_WSFED_MESSAGE,
    constants.ROUTE_PAGE_CONFIRM_CONTACT,
    // Hash deep-links (e.g. Ice Mobile #pageForgotPassword) have no jspPage;
    // skip boot checkPermissions so unauthenticated users are not bounced to login.
    constants.ROUTE_PAGE_FORGOT_PASSWORD,
    constants.ROUTE_FORGOT_PASSWORD_SERVLET,
    constants.ROUTE_PAGE_CHANGE_PASSWORD,
    constants.ROUTE_CHANGE_PASSWORD_SERVLET,
];

const AuthProvider = ({ children }) => {
    const dispatch = useDispatch();
    const location = useLocation();

    // Subscribe to Redux — action creators still dispatch to authenticateReducer
    const reduxAuthSession = useSelector(state => state.authenticateReducer.authSession);
    const reduxUserProfile = useSelector(state => state.authenticateReducer.userProfile);
    const reduxSsoEnvironment = useSelector(state => state.authenticateReducer.ssoEnvironment);

    const [authState, setAuthState] = useState({
        authenticatedUser: null,
        isAuthenticated: false,
        authenticatedApp: '',
        isLauncherAuth: false,
        apps: {},
        userProfile: null,
        ssoEnvironment: '',
        sessionExpired: false,
        errorMessage: '',
        loading: true,
    });

    // Sync Redux → local state when action creators update authenticateReducer
    useEffect(() => {
        const reduxAuthUser = reduxAuthSession.authenticatedUser;
        const reduxIsAuthenticated = reduxAuthSession.isAuthenticated;
        if (reduxAuthUser && Object.keys(reduxAuthUser).length > 0) {
            setAuthState(prev => ({
                ...prev,
                authenticatedUser: reduxAuthUser,
                isAuthenticated: reduxIsAuthenticated,
                authenticatedApp: reduxAuthSession.authenticatedApp || reduxAuthUser.authenticatedApp || '',
                isLauncherAuth: reduxAuthSession.isLauncherAuth,
                apps: reduxAuthUser.apps || prev.apps,
                userProfile: reduxUserProfile || prev.userProfile,
                loading: false,
            }));
        } else if (!reduxIsAuthenticated && authState.isAuthenticated) {
            setAuthState(prev => ({
                ...prev,
                authenticatedUser: null,
                isAuthenticated: false,
                authenticatedApp: '',
                isLauncherAuth: false,
                apps: {},
                userProfile: null,
            }));
        }
    }, [reduxAuthSession, reduxUserProfile]);

    useEffect(() => {
        if (reduxSsoEnvironment && Object.keys(reduxSsoEnvironment).length > 0) {
            setAuthState(prev => ({ ...prev, ssoEnvironment: reduxSsoEnvironment }));
        }
    }, [reduxSsoEnvironment]);

    const checkPermissions = useCallback((pageToGo) => {
        const isFederatedLogin =
            JspUtil.isValueAvailable(window.jspSamlLogin) ||
            JspUtil.isValueAvailable(window.jspImtFederated);
        const preserveInitialRoute = Boolean(getInitialRoute());
        const permissionCheck = isFederatedLogin
            ? actions.checkFederatedPermissions(window.jspLoginApp)
            : actions.checkPermissions(pageToGo, {
                navigateOnSuccess: !preserveInitialRoute,
                navigateOnFailure: !preserveInitialRoute,
            });

        return dispatch(permissionCheck).finally(() => {
            setAuthState(prev => ({ ...prev, loading: false }));
        });
    }, [dispatch]);

    useEffect(() => {
        // Public routes (e.g., /registration) don't require authentication — skip checkPermissions
        const isPublicRoute = PUBLIC_ROUTES.some(route => location.pathname.startsWith(route));
        if (isPublicRoute) {
            setAuthState(prev => ({ ...prev, loading: false }));
            return;
        }
        // App-user relogin intentionally starts without a session permission check.
        if (!reduxAuthSession.isAuthenticated && window.jspIsAppUserRelogin !== 'Y') {
            checkPermissions();
        } else {
            setAuthState(prev => ({ ...prev, loading: false }));
        }
    }, []);

    const logout = useCallback((userId, stayPage, doClear, logoutParentToken) => {
        // Clear local mirror synchronously. logoutUserToken dispatches AUTH_SESSION with an
        // empty user object; the Redux sync effect runs after this render commits, but by
        // then PageLogin's mount effect has already read the stale authState fallback and
        // navigated back to /pageAuthenticated → /pageUserInfo, producing a redirect loop.
        // Resetting here keeps every consumer's first post-logout render in sync.
        setAuthState(prev => ({
            ...prev,
            authenticatedUser: null,
            isAuthenticated: false,
            authenticatedApp: '',
            isLauncherAuth: false,
            apps: {},
            userProfile: null,
        }));
        dispatch(actions.logoutUserToken(userId, stayPage, doClear, logoutParentToken));
    }, [dispatch]);

    const updateAuthState = useCallback((updates) => {
        setAuthState(prev => ({ ...prev, ...updates }));
    }, []);

    // Derive from both sources. Redux is the source of truth once it has *ever* signaled
    // authenticated — every login/logout path goes through AUTH_SESSION. authState is a
    // local mirror that lags one tick (sync useEffect runs after the consumer's render),
    // so OR-falling back to authState after a logout would let stale data drive a redirect
    // loop (PageLogin → PageAuthenticated → PageUserInfo → /pageLogin). We use authState
    // only on the boot path, before AuthProvider.checkPermissions has populated Redux.
    const reduxAuthUser = reduxAuthSession.authenticatedUser;
    const hasReduxAuthUser = reduxAuthUser && Object.keys(reduxAuthUser).length > 0;
    const reduxHasBeenAuthenticatedRef = useRef(false);
    if (reduxAuthSession.isAuthenticated || hasReduxAuthUser) {
        reduxHasBeenAuthenticatedRef.current = true;
    }
    const isAuthenticated = reduxHasBeenAuthenticatedRef.current
        ? reduxAuthSession.isAuthenticated
        : (reduxAuthSession.isAuthenticated || authState.isAuthenticated);
    const authenticatedUser = hasReduxAuthUser
        ? reduxAuthUser
        : (reduxHasBeenAuthenticatedRef.current ? null : authState.authenticatedUser);
    const isLauncherAuth = hasReduxAuthUser
        ? reduxAuthSession.isLauncherAuth
        : (reduxHasBeenAuthenticatedRef.current ? false : (reduxAuthSession.isLauncherAuth || authState.isLauncherAuth));

    const value = useMemo(() => ({
        // Identity
        authenticatedUser,
        isAuthenticated,
        isLauncherAuth,
        authenticatedApp: authState.authenticatedApp || reduxAuthSession.authenticatedApp || '',
        user: authenticatedUser,
        app: authState.authenticatedApp || reduxAuthSession.authenticatedApp || '',
        apps: authState.apps,

        // State
        userProfile: authState.userProfile,
        ssoEnvironment: authState.ssoEnvironment,
        sessionExpired: authState.sessionExpired,
        errorMessage: authState.errorMessage,
        loading: authState.loading,

        // Actions
        checkPermissions,
        logout,
        updateAuthState,
    }), [authState, isAuthenticated, authenticatedUser, isLauncherAuth, reduxAuthSession, checkPermissions, logout, updateAuthState]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthProvider;
